import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import { clx, showSuccessMsg } from "../../../../../utils/Helpers";
import { UIModal } from "../../../../ui";
import {
  bulkCreateAdminUserEventPayments,
  bulkCreateCompanyUserEventPayments,
  createAdminUserEventPayment,
  createCompanyUserEventPayment,
} from "../../../../../network/apis/payroll";
import { uploadPayrollProof } from "./uploadPayrollProof";
import EventPayrollProofOfTransaction from "./EventPayrollProofOfTransaction";

import "./EventPayrollAttachMarkPaidModal.scss";

const EventPayrollAttachMarkPaidModal = ({
  open,
  applicantIds,
  workerName,
  eventId,
  portal,
  labels,
  shared,
  singleSuccessMessage,
  isEventCompleted,
  onClose,
  onSuccess,
}) => {
  const [isUploadReady, setIsUploadReady] = useState(false);
  const [uploadedProofUrl, setUploadedProofUrl] = useState(null);
  const [fileRequiredError, setFileRequiredError] = useState(null);
  const [hasUploadValidationError, setHasUploadValidationError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  useEffect(() => {
    if (!open) {
      setIsUploadReady(false);
      setUploadedProofUrl(null);
      setFileRequiredError(null);
      setHasUploadValidationError(false);
      setSubmitting(false);
      setUploadKey((current) => current + 1);
    }
  }, [open]);

  const handleRequestClose = () => {
    if (submitting) return;
    onClose();
  };

  const isBulk = applicantIds.length > 1;

  const handleConfirm = useCallback(async () => {
    if (!isEventCompleted || hasUploadValidationError) return;

    if (!uploadedProofUrl || !isUploadReady) {
      setFileRequiredError(labels.fileRequired);
      return;
    }

    setSubmitting(true);
    try {
      if (isBulk) {
        const bulkApi =
          portal === "company"
            ? bulkCreateCompanyUserEventPayments
            : bulkCreateAdminUserEventPayments;
        const response = await bulkApi({
          eventId,
          userIds: applicantIds,
          paymentProofUrl: uploadedProofUrl,
        });
        if (response?.data?.message) {
          showSuccessMsg(response.data.message, true);
        }
      } else {
        const createApi =
          portal === "company"
            ? createCompanyUserEventPayment
            : createAdminUserEventPayment;
        await createApi({
          eventId,
          userId: applicantIds[0],
          paymentProofUrl: uploadedProofUrl,
        });
        if (singleSuccessMessage) {
          showSuccessMsg(singleSuccessMessage, false);
        }
      }
      onSuccess?.();
      onClose();
    } catch {
      // Keep dialog open; interceptors handle errors when API is wired.
    } finally {
      setSubmitting(false);
    }
  }, [
    applicantIds,
    eventId,
    hasUploadValidationError,
    isBulk,
    isEventCompleted,
    isUploadReady,
    labels.fileRequired,
    onClose,
    onSuccess,
    portal,
    singleSuccessMessage,
    uploadedProofUrl,
  ]);

  const subtitle = isBulk
    ? labels.subtitleBulk.replace("{count}", String(applicantIds.length))
    : labels.subtitleSingle.replace("{name}", workerName || "-");

  return (
    <UIModal
      open={open}
      type="info"
      title={labels.title}
      description={subtitle}
      cancelLabel={shared.cancel}
      confirmLabel={labels.markAsPaid}
      onClose={handleRequestClose}
      onCancel={handleRequestClose}
      onConfirm={handleConfirm}
      cancelButtonProps={{ disabled: submitting }}
      confirmButtonProps={{
        disabled:
          submitting ||
          !isEventCompleted ||
          hasUploadValidationError ||
          !isUploadReady,
        loading: submitting,
      }}
      PaperProps={{
        className: clx("event-payroll-attach-mark-paid-modal"),
      }}
      aria-labelledby="event-payroll-attach-mark-paid-modal-title"
      disableEscapeKeyDown={submitting}
    >
      <EventPayrollProofOfTransaction
        key={uploadKey}
        labels={labels}
        fullWidth
        disabled={submitting || !isEventCompleted}
        requiredError={fileRequiredError}
        uploadFile={(file, onProgress) =>
          uploadPayrollProof(file, { onProgress })
        }
        onReadyChange={setIsUploadReady}
        onUploadComplete={(file, url) => {
          setUploadedProofUrl(url);
          setFileRequiredError(null);
        }}
        onFileClear={() => {
          setUploadedProofUrl(null);
          setIsUploadReady(false);
        }}
        onValidationChange={setHasUploadValidationError}
        onUploadAttemptWithoutFile={() =>
          setFileRequiredError(labels.fileRequired)
        }
      />
    </UIModal>
  );
};

EventPayrollAttachMarkPaidModal.propTypes = {
  open: PropTypes.bool.isRequired,
  applicantIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  workerName: PropTypes.string,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  portal: PropTypes.oneOf(["admin", "company"]),
  labels: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  singleSuccessMessage: PropTypes.string,
  isEventCompleted: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

EventPayrollAttachMarkPaidModal.defaultProps = {
  workerName: null,
  eventId: null,
  portal: "admin",
  singleSuccessMessage: "",
  isEventCompleted: false,
  onSuccess: undefined,
};

export default EventPayrollAttachMarkPaidModal;
